import { compact, uniq, values } from 'lodash';
import type { CreateCandidate, CreateFormState } from '../../services/create/createFormReducer';
import type {
  CommonCreateValidationErrors,
  CreateValidationErrors,
  SpecificCreateValidationErrors,
} from '../../services/create/createReducer';
import type { BookingValidationErrors } from '../../services/update-form/updateBookingReducer';
import type { BookingFormState } from '../../services/update-form/updateFormReducer';

export interface FieldValidationError {
  description: string;
  field: string | null;
}

export function getCreateValidationErrorsAsArray(
  errors: CreateValidationErrors | null | undefined,
): FieldValidationError[] {
  if (!errors) {
    return [];
  }

  const result: FieldValidationError[] = [];

  for (const field in errors.common) {
    const description = errors.common[field as keyof CommonCreateValidationErrors];
    if (description) {
      result.push({ field, description });
    }
  }

  for (const index in errors.specific) {
    const specific = errors.specific[index];
    if (specific.goods) {
      for (const itemErrors of Object.values(specific.goods)) {
        if (!itemErrors) {
          continue;
        }
        for (const [key, message] of Object.entries(itemErrors)) {
          if (
            message &&
            !result.some(error => error.field === key && error.description === message)
          ) {
            result.push({ field: key, description: message });
          }
        }
      }
    }
  }

  return result;
}

export function getSpecificCreateValidationErrorsAsStringArray(
  errors: SpecificCreateValidationErrors | null | undefined,
  index: number,
): string[] {
  const specific = errors?.[index];
  if (!specific) {
    return [];
  }

  const result: string[] = [];

  if (specific.goods) {
    for (const itemErrors of Object.values(specific.goods)) {
      result.push(...compact(values(itemErrors)));
    }
  }

  return uniq(result);
}

export function getBookingValidationErrorsAsStringArray(
  errors: BookingValidationErrors | null | undefined,
): string[] {
  if (!errors) return [];

  const result: string[] = [];

  if (errors.sailing) result.push(errors.sailing);
  if (errors.departureDate) result.push(errors.departureDate);

  if (errors.goods) {
    for (const itemErrors of Object.values(errors.goods)) {
      result.push(...compact(values(itemErrors)));
    }
  }

  return uniq(result);
}

export function getCommonCreateValidationKey(
  field: keyof CreateFormState,
): keyof CommonCreateValidationErrors | null {
  switch (field) {
    case 'departureDate':
      return 'departureDate';
    case 'sailing':
      return 'sailing';
    default:
      return null;
  }
}

export function getSpecificCreateValidationKey(
  field: keyof CreateCandidate,
): keyof SpecificCreateValidationErrors[number] | null {
  switch (field) {
    case 'goods':
      return 'goods';
    default:
      return null;
  }
}

export function getUpdateValidationKey(
  field: keyof BookingFormState,
): keyof BookingValidationErrors | null {
  switch (field) {
    case 'departureDate':
      return 'departureDate';
    case 'sailing':
      return 'sailing';
    case 'goods':
      return 'goods';
    default:
      return null;
  }
}

export function clearUpdateFieldFromValidationErrors(
  field: keyof BookingFormState,
  errors: BookingValidationErrors,
) {
  const key = getUpdateValidationKey(field);

  if (key) {
    delete errors[key];
  }
}

export function clearCommonCreateFieldFromValidationErrors(
  field: keyof CreateFormState,
  errors: CreateValidationErrors,
) {
  const key = getCommonCreateValidationKey(field);

  if (key) {
    delete errors.common[key];
  }
}

export function clearSpecificCreateFieldFromValidationErrors(
  field: keyof CreateCandidate,
  errors: CreateValidationErrors,
  index: number,
) {
  const key = getSpecificCreateValidationKey(field);

  if (key && errors.specific[index]) {
    delete errors.specific[index][key];
  }
}
